/* Shipping Protection per merchandise line; Priority Production once per order. */
(() => {
  if (window.GPShippingProtection || !document.querySelector('[data-shipping-protection]') || !document.querySelector('[data-item-protection-config]')) return;
  const root = window.Shopify?.routes?.root || '/';
  const widgets = () => [...document.querySelectorAll('[data-shipping-protection]')];
  const handleOf = box => box.dataset.protectionHandle || 'shipping-protection';
  const identityOf = box => box.dataset.serviceIdentity || handleOf(box);
  const widget = identity => widgets().find(box => identityOf(box) === identity);
  const handles = new Set(['shipping-protection', 'priority-production']);
  const isProtection = item => handles.has(item.handle);
  const choiceAttribute = 'gp_item_shipping_choices';
  let busy = false;
  let uncertain = false;
  let savedControls = [];
  let savedInert = false;
  let expressInert = false;

  function message(text = '', error = false, identity) {
    widgets().filter(box => !identity || identityOf(box) === identity).forEach(box => {
      const status = box.querySelector('.gp-protection__status');
      status.textContent = text;
      status.toggleAttribute('data-error', error);
    });
  }
  function reveal() {
    widgets().forEach(box => {
      const input = box.querySelector('input');
      box.hidden = Number(box.dataset.merchandiseCount) === 0 || (!box.dataset.variantId && !input.checked);
      if (input.disabled && !input.checked) message(`${box.dataset.protectionTitle || 'This service'} is currently unavailable.`, false, identityOf(box));
    });
  }
  function start(nativeQuantity = false) {
    if (busy) return false;
    // Dawn debounces quantity edits; let those finish before changing line indexes.
    if (!nativeQuantity && [...document.querySelectorAll('cart-items .quantity__input')].some(input => input.value !== input.defaultValue)) return false;
    busy = true;
    const items = document.querySelector('cart-items');
    savedInert = items?.inert || false;
    if (items) items.inert = true;
    const express = document.querySelector('.cart__dynamic-checkout-buttons');
    if (!uncertain) expressInert = express?.inert || false;
    if (express) express.inert = true;
    savedControls = [...document.querySelectorAll('[data-protection-toggle], #checkout')].map(el => [el, el.disabled]);
    savedControls.forEach(([el]) => { el.disabled = true; });
    widgets().forEach(box => box.setAttribute('aria-busy', 'true'));
    message();
    return true;
  }
  function end() {
    busy = false;
    const items = document.querySelector('cart-items');
    if (items) items.inert = savedInert;
    const express = document.querySelector('.cart__dynamic-checkout-buttons');
    if (express) express.inert = uncertain || expressInert;
    savedControls.forEach(([el, disabled]) => { if (el.isConnected) el.disabled = disabled; });
    savedControls = [];
    widgets().forEach(box => box.removeAttribute('aria-busy'));
    reveal();
  }
  function sections() {
    return document.querySelector('cart-items')?.getSectionsToRender() || [];
  }
  async function request(path, data) {
    const response = await fetch(root + path, data ? {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(data),
    } : { headers: { Accept: 'application/json' }, cache: 'no-store' });
    const result = await response.json();
    if (!response.ok || result.errors || result.status >= 400) throw new Error(result.description || 'Unable to update order services.');
    return result;
  }
  function sectionParams() {
    return { sections: sections().map(section => section.section), sections_url: location.pathname };
  }
  function choices(cart) {
    try {
      const value = JSON.parse(cart.attributes?.[choiceAttribute] || '{}');
      return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
    } catch { return {}; }
  }
  async function merchandise(cart) {
    // Key and price can change with discounts/quantity. Hash the actual configuration
    // instead, without modifying Teeinblue's original line properties.
    const seen = new Map();
    const records = await Promise.all(cart.items.filter(item => !isProtection(item)).map(async item => {
      const properties = Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b));
      const source = JSON.stringify([item.variant_id, item.selling_plan_allocation?.selling_plan?.id || null, properties]);
      const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(source));
      const hash = [...new Uint8Array(digest)].map(byte => byte.toString(16).padStart(2, '0')).join('');
      return { item, hash };
    }));
    return records.map(({ item, hash }) => {
      const occurrence = (seen.get(hash) || 0) + 1;
      seen.set(hash, occurrence);
      return { item, identity: `${hash}-${occurrence}` };
    });
  }
  async function syncItemWidgets(cart) {
    const records = await merchandise(cart);
    const config = document.querySelector('[data-item-protection-config]');
    const available = config?.dataset.available === 'true';
    widgets().filter(box => box.dataset.scope === 'item').forEach(box => {
      const record = records.find(record => record.item.key === box.dataset.parentKey);
      if (!record) return;
      box.dataset.serviceIdentity = record.identity;
      const fee = cart.items.find(item => item.handle === 'shipping-protection' && item.properties?._gp_shipping_parent === record.identity);
      const input = box.querySelector('input');
      input.checked = input.defaultChecked = !!fee;
      input.disabled = !available && !fee;
      if (fee) {
        box.querySelector('.gp-protection__price').textContent = `${new Intl.NumberFormat('en-US', { style: 'currency', currency: cart.currency }).format(fee.final_line_price / 100)} ${cart.currency}`;
      }
    });
  }
  async function normalize(cart, addMissing = true) {
    if (cart.errors || !cart.items) return cart;
    const records = await merchandise(cart);
    const previousChoices = choices(cart);
    const nextChoices = Object.fromEntries(records.filter(record => typeof previousChoices[record.identity] === 'boolean').map(record => [record.identity, previousChoices[record.identity]]));
    const config = document.querySelector('[data-item-protection-config]');
    const available = config?.dataset.available === 'true';
    const variantId = Number(config?.dataset.variantId);
    const desired = new Map(records.filter(record => nextChoices[record.identity] !== false).map(record => [record.identity, record]));
    const updates = {};
    const seen = new Set();
    let prioritySeen = false;
    cart.items.filter(isProtection).forEach(item => {
      let quantity = 0;
      if (item.handle === 'priority-production') {
        quantity = records.length && !prioritySeen ? 1 : 0;
        prioritySeen = true;
      } else {
        const identity = item.properties?._gp_shipping_parent;
        if (desired.has(identity) && !seen.has(identity)) {
          quantity = 1;
          seen.add(identity);
        }
      }
      if (item.quantity !== quantity) updates[item.key] = quantity;
    });
    const attributesChanged = JSON.stringify(previousChoices) !== JSON.stringify(nextChoices);
    if (Object.keys(updates).length || attributesChanged) {
      cart = await request('cart/update.js', { updates, ...(attributesChanged ? { attributes: { [choiceAttribute]: JSON.stringify(nextChoices) } } : {}), ...sectionParams() });
    }
    const missing = addMissing && available && variantId ? [...desired.values()].filter(record => !seen.has(record.identity)) : [];
    if (missing.length) {
      await request('cart/add.js', { items: missing.map(record => ({
        id: variantId, quantity: 1,
        properties: { _gp_shipping_parent: record.identity, 'For item': record.item.product_title },
      })) });
      cart = await request('cart.js');
    }
    return cart;
  }
  async function render(cart) {
    const targets = sections();
    let html = cart.sections;
    if (!html || targets.some(section => !html[section.section])) {
      const url = new URL(root + 'cart', location.origin);
      url.searchParams.set('sections', targets.map(section => section.section).join(','));
      const response = await fetch(url, { cache: 'no-store' });
      if (!response.ok) throw new Error('Unable to refresh cart.');
      html = await response.json();
    }
    const items = document.querySelector('cart-items');
    const replacements = targets.map(section => {
      const source = new DOMParser().parseFromString(html[section.section] || '', 'text/html').querySelector(section.selector);
      const parent = document.getElementById(section.id);
      const target = parent?.querySelector(section.selector) || parent;
      if (!source || !target) throw new Error('Unable to refresh cart.');
      return { target, markup: source.innerHTML };
    });
    replacements.forEach(({ target, markup }) => items.renderCartSection(target, markup));
    items.classList.toggle('is-empty', cart.item_count === 0);
    document.getElementById('main-cart-footer')?.classList.toggle('is-empty', cart.item_count === 0);
    await syncItemWidgets(cart);
    uncertain = false;
    reveal();
  }
  async function recover(identity) {
    // A lost response can follow a successful write. Re-read before restoring the UI.
    try { await render(await normalize(await request('cart.js'), false)); }
    catch { uncertain = true; }
    message(uncertain ? 'Unable to confirm your cart. Please reload before checkout.' : 'Unable to complete the update. Your current cart is shown; please try again.', true, identity);
  }
  async function toggle(input) {
    const selected = input.checked;
    const box = input.closest('[data-shipping-protection]');
    const handle = handleOf(box);
    const identity = identityOf(box);
    const perItem = box.dataset.scope === 'item';
    const title = box.dataset.protectionTitle || 'Shipping Protection';
    const variantId = Number(box.dataset.variantId);
    if (!start()) { input.checked = input.defaultChecked; return; }
    message('Updating…', false, identity);
    try {
      let cart = await request('cart.js');
      const protection = cart.items.filter(item => item.handle === handle);
      const hasProducts = cart.items.some(item => !isProtection(item));
      if (perItem) {
        const records = await merchandise(cart);
        if (!records.some(record => record.identity === identity)) throw new Error('This item is no longer in the cart.');
        cart = await request('cart/update.js', { attributes: { [choiceAttribute]: JSON.stringify({ ...choices(cart), [identity]: selected }) } });
      } else if (selected && hasProducts && !protection.length) {
        if (!variantId) throw new Error(`${title} is unavailable.`);
        await request('cart/add.js', { items: [{ id: variantId, quantity: 1 }] });
        cart = await request('cart.js');
      } else if (!selected && protection.length) {
        const updates = Object.fromEntries(protection.map(item => [item.key, 0]));
        cart = await request('cart/update.js', { updates, ...sectionParams() });
      }
      cart = await normalize(cart);
      await render(cart);
      message(selected && hasProducts ? `${title} added for this ${perItem ? 'item' : 'order'}.` : `${title} removed.`, false, identity);
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'shipping-protection', cartData: cart });
    } catch { await recover(identity); }
    finally {
      end();
      widget(identity)?.querySelector('[data-protection-toggle]')?.focus({ preventScroll: true });
    }
  }
  async function reconcile() {
    if (!start()) return;
    try {
      const cart = await request('cart.js');
      await render(await normalize(cart));
    } catch { await recover(); }
    finally { end(); }
  }
  window.GPShippingProtection = { start, end, normalize, recover, sync: syncItemWidgets };
  document.documentElement.classList.add('gp-protection-ready');
  document.addEventListener('change', event => {
    if (event.target.matches('[data-protection-toggle]')) toggle(event.target);
  });
  // Also cover checkout links in the preview popup and accelerated checkout controls.
  document.addEventListener('click', event => {
    const checkout = event.target.closest('#checkout, [name="checkout"], a[href*="/checkout"], .cart__dynamic-checkout-buttons');
    if (checkout && (busy || uncertain)) {
      event.preventDefault(); event.stopImmediatePropagation();
      message(uncertain ? 'Please reload the page to confirm your cart before checkout.' : 'Please wait for your cart to finish updating.', uncertain);
    }
  }, true);
  document.addEventListener('submit', event => {
    if (event.target.id === 'cart' && (busy || uncertain)) {
      event.preventDefault(); event.stopImmediatePropagation();
    }
  }, true);
  subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
    if (!['cart-items', 'shipping-protection'].includes(event.source)) reconcile();
  });
  window.addEventListener('pageshow', event => { if (event.persisted) reconcile(); });
  reveal();
  reconcile();
})();
